import { Component, OnInit } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { Modal } from 'bootstrap';
import { finalize } from 'rxjs';
import { ApiMeta } from '../../_core/_model/api-response';
import { MainCurrencyDto } from '../../_core/_model/currency';
import { OrderDto, CreateOrder } from '../../_core/_model/order';
import { UserDto } from '../../_core/_model/users';
import { WalletDetailsDto } from '../../_core/_model/wallet';
import { ApiService } from '../../_core/_services/api.service';
import { AuthService } from '../../_core/_services/auth.service';
import { FormatService } from '../../_core/_services/format';
import { NotificationService } from '../../_core/_services/notification.service';
import { OrderPrefill, OrderDraftService } from '../../_core/_services/order-draft-service';

@Component({
  selector: 'app-all-orders',
  standalone: false,
  templateUrl: './all-orders.html',
  styleUrl: './all-orders.css',
})

export class AllOrders implements OnInit {
  loading: boolean = false;
  errorObject: {error: boolean, message: string, showButton?: boolean, buttonText?: string, buttonRoute?: string } | null = null;

  currencies: MainCurrencyDto[] = [];

  searchForm!: FormGroup;

  private updating = false;

  showSearchResult: boolean = false;

  loadingAllOrders: boolean = true;
  loadingOpenOrders: boolean = true;
  loadingPartialOrders: boolean = true;
  loadingCompletedOrders: boolean = true;
  loadingCancelledOrders: boolean = true;
  loadingFailedOrders: boolean = true;
  loadingSearchResults: boolean = false;

  allOrdersPage: number = 1;
  openOrdersPage: number = 1;
  partialOrdersPage: number = 1;
  completedOrdersPage: number = 1;
  cancelledOrdersPage: number = 1;
  failedOrdersPage: number = 1;
  searchResultsPage: number = 1;

  allOrders: OrderDto[] = [];
  openOrders: OrderDto[] = [];
  partialOrders: OrderDto[] = [];
  completedOrders: OrderDto[] = [];
  cancelledOrders: OrderDto[] = [];
  failedOrders: OrderDto[] = [];
  searchResults: OrderDto[] = [];

  allOrdersMeta!: ApiMeta;
  openOrdersMeta!: ApiMeta;
  partialOrdersMeta!: ApiMeta;
  completedOrdersMeta!: ApiMeta;
  cancelledOrdersMeta!: ApiMeta;
  failedOrdersMeta!: ApiMeta;
  searchResultsMeta!: ApiMeta;

  userData!: UserDto;
  walletId!: string;
  wallet: WalletDetailsDto | null = null;

  activeTab: string = 'tabAll';
  url: any;

  constructor(
    private fb: FormBuilder,
    private api: ApiService,
    private format: FormatService,
    private router: Router,
    private notifyService: NotificationService,
  ) {
    this.searchForm = this.fb.nonNullable.group({
      searchQuery: ['', Validators.required],
    });

    this.url = this.router.url.slice(1);
  }

  get searchQuery() { return this.searchForm.get('searchQuery'); }

  refresh() {
    this.loadOrders();
    this.loadOrdersByStatus('Open');
    this.loadOrdersByStatus('Partial');
    this.loadOrdersByStatus('Completed');
    this.loadOrdersByStatus('Cancelled');
    this.loadOrdersByStatus('Failed');
  }

  ngOnInit(): void {
    this.loadOrders();
    this.loadOrdersByStatus('Open');
    this.loadOrdersByStatus('Partial');
    this.loadOrdersByStatus('Completed');
    this.loadOrdersByStatus('Cancelled');
    this.loadOrdersByStatus('Failed');
  }

  getOrder(status: string | null, page: number) {
    this.setPage(status!, page);
    if (status === null) this.loadOrders();
    else this.loadOrdersByStatus(status);
  }

  loadOrders() {
    this.loadingAllOrders = true;
    this.errorObject = null;

    this.activeTab = 'tabAll';

    this.showSearchResult = false;
    this.loadingSearchResults = false;

    this.api.getAdminOrders(this.getPage(null)).subscribe({
      next: (res) => { 
        const orders = res?.data ?? [];
        this.allOrders = orders;
        const meta: ApiMeta | any = res?.meta;
        this.setOrdersMeta(null, meta);
      },
      error: (e) => {
        this.errorObject = {
          error: true,
          message: e?.error?.Error?.Details ?? 'Failed to load orders'
        };
        this.notifyService.showError(this.errorObject.message, "Error");
      },
      complete: () => (this.loadingAllOrders = false),
    });
  }

  loadOrdersByStatus(status: string) {
    this.errorObject = null;
    this.setLoading(status, true);

    this.activeTab = 'tabAll';

    this.showSearchResult = false;
    this.loadingSearchResults = false;

    this.api.getAdminOrdersByStatus(status, this.getPage(status)).pipe(
      finalize(() => {
        this.setLoading(status, false);
      })
    ).subscribe({
      next: (res) => {
        const orders = res?.data ?? [];
        const meta: ApiMeta | any = res?.meta;
        this.setOrders(status, orders);
        this.setOrdersMeta(status, meta);
      },
      error: (e) => {
        const msg = e?.error?.Error?.Details ?? `Failed to load ${status} orders`;
        this.notifyService.showError(msg, `${status} Error`);
      }
    });
  }

  private setLoading(status: string | null, isLoading: boolean): void {
    if (status === 'Open') this.loadingOpenOrders = isLoading;
    else if (status === 'Partial') this.loadingPartialOrders = isLoading;
    else if (status === 'Completed') this.loadingCompletedOrders = isLoading;
    else if (status === 'Cancelled') this.loadingCancelledOrders = isLoading;
    else if (status === 'Failed') this.loadingFailedOrders = isLoading;
    else this.loadingAllOrders = isLoading; // if you have it
  }

  private setOrders(status: string | null, orders: OrderDto[]): void {
    if (status === 'Open') this.openOrders = orders;
    else if (status === 'Partial') this.partialOrders = orders;
    else if (status === 'Completed') this.completedOrders = orders;
    else if (status === 'Cancelled') this.cancelledOrders = orders;
    else if (status === 'Failed') this.failedOrders = orders;
    else this.allOrders = orders;
  }

  private setOrdersMeta(status: string | null, meta: ApiMeta): void {
    if (status === 'Open') this.openOrdersMeta = meta;
    else if (status === 'Partial') this.partialOrdersMeta = meta;
    else if (status === 'Completed') this.completedOrdersMeta = meta;
    else if (status === 'Cancelled') this.cancelledOrdersMeta = meta;
    else if (status === 'Failed') this.failedOrdersMeta = meta;
    else this.allOrdersMeta = meta;
  }

  private getPage(status: string | null): number {
    if (status === 'Open') return this.openOrdersPage;
    else if (status === 'Partial') return this.partialOrdersPage;
    else if (status === 'Completed') return this.completedOrdersPage;
    else if (status === 'Cancelled') return this.cancelledOrdersPage;
    else if (status === 'Failed') return this.failedOrdersPage;
    else return this.allOrdersPage;
  }

  private setPage(status: string, page:number): void {
    if (status === 'Open') this.openOrdersPage = page;
    else if (status === 'Partial') this.partialOrdersPage = page;
    else if (status === 'Completed') this.completedOrdersPage = page;
    else if (status === 'Cancelled') this.cancelledOrdersPage = page;
    else if (status === 'Failed') this.failedOrdersPage = page;
    else this.allOrdersPage = page;
  }

  submitSearch() {
    if (this.searchForm.invalid) return;

    const query = this.searchQuery?.value.trim();
    if (!query) return;

    this.activeTab = 'tabSearch';

    this.showSearchResult = true;
    this.loadingSearchResults = true;
    this.errorObject = null;

    this.api.searchAdminOrders(query, this.searchResultsPage).pipe(
      finalize(() => {
        this.loadingSearchResults = false;
      })
    ).subscribe({
      next: (res) => {
        const orders = res?.data ?? [];
        if (orders.length > 0) {
          this.searchResults = orders;
          this.notifyService.showSuccess(`Found ${orders.length} order(s) matching "${query}"`, "Search Results");
        } else {
          this.notifyService.showInfo(`No orders found matching "${query}"`, "Search Results");
        }
      },
      error: (e) => {
        const msg = e?.error?.Error?.Details ?? 'Failed to search orders';
        this.notifyService.showError(msg, "Error");
      }
    });
  }

  getOrderStatusClass(status: string): string {
    return this.format.getOrderStatusClass(status);
  }

  viewOrder(orderId: string) {
    this.router.navigate(['/admin/orders/manage', orderId]);
  }

  shortId(id: string, start = 6, end = 4): string {
    if (!id) return '';
    if (id.length <= start + end) return id;

    return `${id.slice(0, start)}…${id.slice(-end)}`;
  }

  copyToClipboard(text: string) {
    if (!text) return;

    navigator.clipboard.writeText(text).then(() => {
      this.notifyService?.showSuccess?.('Order ID copied', 'Copied');
    }).catch(() => {
      this.notifyService?.showError?.('Failed to copy Order ID', 'Error');
    });
  }
}
